import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Users, Target, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const AboutPage = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#F9F9F7]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-8">
                            <Link to="/features" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Features</Link>
                            <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Pricing</Link>
                            <Link to="/about" className="text-sm font-medium text-[#1A4D2E] transition-colors">About</Link>
                            <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Contact</Link>
                        </nav>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/login')}
                                className="text-[#1A4D2E] hover:bg-green-50"
                            >
                                Log In
                            </Button>
                            <Button
                                onClick={() => navigate('/register')}
                                className="bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20"
                            >
                                Sign Up Free
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-6 md:px-12 bg-white">
                <div className="max-w-4xl mx-auto text-center space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-100 mb-4">
                        <span className="text-sm font-medium text-[#1A4D2E]">Our Mission</span>
                    </div>
                    <h1 className="font-serif text-4xl md:text-6xl text-[#1F2937] leading-tight">
                        Democratizing financial literacy through <span className="text-[#1A4D2E]">risk-free practice</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        We believe everyone implies the right to understand the markets before risking their hard-earned money. FictoInvest provides a safe harbor to learn, fail, and grow.
                    </p>
                </div>
            </section>

            {/* Values Grid */}
            <section className="py-20 px-6 md:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-8">
                        <Card className="bg-white border-none shadow-lg shadow-green-900/5">
                            <CardContent className="p-8 space-y-4">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <Target className="w-6 h-6 text-[#1A4D2E]" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-[#1F2937]">Education First</h3>
                                <p className="text-gray-600">
                                    We prioritize learning over gamification. Our tools are designed to teach real market mechanics, not just flashy numbers.
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-none shadow-lg shadow-green-900/5">
                            <CardContent className="p-8 space-y-4">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-[#1A4D2E]" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-[#1F2937]">Safe Environment</h3>
                                <p className="text-gray-600">
                                    Psychological safety is key to learning. By removing financial risk, we allow users to experiment with bold strategies.
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-none shadow-lg shadow-green-900/5">
                            <CardContent className="p-8 space-y-4">
                                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                    <Users className="w-6 h-6 text-[#1A4D2E]" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-[#1F2937]">Community Driven</h3>
                                <p className="text-gray-600">
                                    Investing shouldn't be lonely. Our platform encourages sharing portfolios and learning from top performers on the leaderboard.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Team/Story Section */}
            <section className="py-20 px-6 md:px-12 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#C1F23D] rounded-3xl transform rotate-3 translate-x-2 translate-y-2"></div>
                            <img
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800"
                                alt="Team working together"
                                className="relative rounded-3xl shadow-xl"
                            />
                        </div>
                        <div className="space-y-6">
                            <h2 className="font-serif text-3xl md:text-5xl text-[#1F2937]">Built by traders, for learners</h2>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                FictoInvest started when we realized that most "paper trading" apps were either too complex for beginners or too simple to be useful.
                            </p>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                We set out to build the "Duolingo for Investing" — a platform that makes financial literacy accessible, engaging, and most importantly, free from the fear of loss.
                            </p>
                            <Button
                                onClick={() => navigate('/register')}
                                className="mt-4 bg-[#1A4D2E] text-white hover:bg-[#143d24] px-8 py-6 text-lg"
                            >
                                Join Our Mission <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 border-t border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </div>
                        <div className="flex gap-8">
                            <Link to="/features" className="text-gray-600 hover:text-[#1A4D2E]">Features</Link>
                            <Link to="/pricing" className="text-gray-600 hover:text-[#1A4D2E]">Pricing</Link>
                            <Link to="/about" className="text-gray-600 hover:text-[#1A4D2E]">About</Link>
                            <Link to="/contact" className="text-gray-600 hover:text-[#1A4D2E]">Contact</Link>
                            <Link to="/privacy" className="text-gray-600 hover:text-[#1A4D2E]">Privacy</Link>
                        </div>
                        <p className="text-sm text-gray-500">
                            © {new Date().getFullYear()} FictoInvest. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;
